import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import printJS from 'print-js';

import { WarehouseLabelApi } from 'api/warehouseLabels';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Notifications } from 'shared';
import { useCompanySourceOptionsByCatalogItemQuery } from 'shared/hooks/inventory/useCompanySourceOptionsByCatalogItemQuery';
import { useCompanySourceTransferMutation } from 'shared/hooks/inventory/useCompanySourceTransferMutation';
import {
    PALLETS_BY_LOCATION_AND_LOT_QUERY_KEY,
    usePalletsByLocationAndLotQuery,
} from 'shared/hooks/warehouse/usePalletsByLocationAndLotQuery';

const EMPTY_PALLETS = [];
const labelApi = new WarehouseLabelApi();

/**
 * @param {Blob} blob
 */
function printPdfBlob(blob) {
    const fr = new FileReader();
    fr.addEventListener(
        'load',
        () => {
            const tmpPDFdata = fr.result.substr(fr.result.indexOf('base64,') + 7);
            printJS({ printable: tmpPDFdata, type: 'pdf', base64: true });
        },
        false,
    );
    fr.readAsDataURL(blob);
}

/**
 * @param {Record<string, unknown>} row
 * @returns {number}
 */
function readAvailableCases(row) {
    const raw = row.numCases ?? row.cases;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {number}
 */
function readAvailableLooseUnits(row) {
    const raw = row.numLooseUnits ?? row.looseUnits;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
}

/**
 * @param {Record<string, unknown>} row
 * @param {number} index
 * @returns {string}
 */
function getInventoryRowKey(row, index) {
    if (row.palletId !== undefined && row.palletId !== null && row.palletId !== '') {
        return `pallet-${row.palletId}`;
    }
    if (row.id !== undefined && row.id !== null && row.id !== '') {
        return `id-${row.id}`;
    }
    return `case-${index}`;
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
function parseNonNegativeInt(raw) {
    if (raw === '' || raw === undefined || raw === null) {
        return 0;
    }
    const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return 0;
    }
    return Math.floor(parsed);
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
function readInventoryCount(raw) {
    return parseNonNegativeInt(raw);
}

/**
 * @typedef {Object} InventoryRowSelection
 * @property {boolean} selected
 * @property {number} numCases
 * @property {number} numLooseUnits
 */

/**
 * Transfer inventory between company sources at a warehouse location.
 *
 * @param {Object} props
 * @param {number} props.locationId
 * @param {string} props.locationName
 * @param {string} [props.externalSku]
 * @param {number | string} [props.packagingId]
 * @param {number | string} [props.lotId]
 * @param {number} [props.numPallets]
 * @param {number} [props.numCases]
 * @param {number} [props.numLooseUnits]
 * @param {Array<Record<string, unknown>>} [props.inventoryRows] - Optional override; otherwise derived from lot summary or pallet fetch
 * @param {(closeModal?: boolean) => void} props.onSuccess - Refreshes inventory after a successful transfer
 * @param {() => void} props.onCancel - Closes the action modal without triggering another refresh
 */
function CompanySourceTransferAction({
    currentCompanySourceId,
    currentCompanySourceName,
    externalSku,
    inventoryRows: inventoryRowsOverride,
    locationId,
    locationName,
    lotId,
    numCases = 0,
    numLooseUnits = 0,
    numPallets = 0,
    onCancel,
    onSuccess,
    packagingId,
}) {
    const { user } = useContext(UserContext);
    const queryClient = useQueryClient();
    const [notification, setNotification] = useState({ text: null, level: null });
    const [targetCompanySourceId, setTargetCompanySourceId] = useState('');
    const [validationError, setValidationError] = useState(/** @type {string | null} */ (null));
    const [rowSelections, setRowSelections] = useState(/** @type {Record<string, InventoryRowSelection>} */ ({}));
    const [transferResult, setTransferResult] = useState(
        /** @type {{ lotId?: number, palletIds?: number[], transferredCases: number } | null} */ (null),
    );
    const [numLabels, setNumLabels] = useState(1);
    const [printingLabels, setPrintingLabels] = useState(false);
    const pendingTransferredCasesRef = useRef(0);

    const palletCount = readInventoryCount(numPallets);
    const summaryCases = readInventoryCount(numCases);
    const summaryLooseUnits = readInventoryCount(numLooseUnits);
    const shouldFetchPallets = palletCount > 0;

    const {
        data: pallets = EMPTY_PALLETS,
        error: palletsQueryError,
        isError: palletsError,
        isFetching: palletsFetching,
        isLoading: palletsLoading,
    } = usePalletsByLocationAndLotQuery({
        facilityId: user?.facilityID,
        locationId,
        lotId,
        enabled: shouldFetchPallets && !inventoryRowsOverride?.length,
    });

    const normalizedRows = useMemo(() => {
        if (Array.isArray(inventoryRowsOverride) && inventoryRowsOverride.length) {
            return inventoryRowsOverride;
        }

        // A location will either have pallets or cases/loose units, but not both
        if (shouldFetchPallets) {
            return pallets.map((pallet) => ({
                palletId: pallet.palletId,
                numCases: pallet.numCases,
                numLooseUnits: pallet.numLooseUnits,
            }));
        }

        if (palletCount === 0 && (summaryCases > 0 || summaryLooseUnits > 0)) {
            return [{ numCases: summaryCases, numLooseUnits: summaryLooseUnits }];
        }

        return [];
    }, [inventoryRowsOverride, pallets, palletCount, shouldFetchPallets, summaryCases, summaryLooseUnits]);

    const rowMeta = useMemo(
        () =>
            normalizedRows.map((row, index) => ({
                key: getInventoryRowKey(row, index),
                row,
                availableCases: readAvailableCases(row),
                availableLooseUnits: readAvailableLooseUnits(row),
            })),
        [normalizedRows],
    );

    const inventoryRowsKey = useMemo(
        () =>
            rowMeta
                .map(
                    ({ availableCases, availableLooseUnits, key }) => `${key}:${availableCases}:${availableLooseUnits}`,
                )
                .join('|'),
        [rowMeta],
    );

    const {
        data: companySourceOptions = [],
        error: companySourceOptionsQueryError,
        isError: companySourceOptionsError,
        isLoading: companySourceOptionsLoading,
    } = useCompanySourceOptionsByCatalogItemQuery({
        externalSku,
        packagingId,
    });

    const effectiveCompanySourceOptions = useMemo(
        () => companySourceOptions.filter((option) => option.value !== currentCompanySourceId),
        [companySourceOptions, currentCompanySourceId],
    );

    const transferMutation = useCompanySourceTransferMutation({
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({ queryKey: [PALLETS_BY_LOCATION_AND_LOT_QUERY_KEY] });
            const transferredCases = pendingTransferredCasesRef.current;
            setTransferResult({
                lotId: data?.lotId,
                palletIds: Array.isArray(data?.palletIds) ? data.palletIds : [],
                transferredCases,
            });
            setNumLabels(Math.max(transferredCases, 1));
            if (onSuccess instanceof Function) {
                onSuccess(false);
            }
        },
        onError: (error) => {
            setNotification({ text: error.message, level: 'error' });
        },
    });

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('CompanySourceTransferAction initialized incorrectly: onSuccess is not a function');
        }
    }, [onSuccess]);

    useEffect(() => {
        if (companySourceOptionsError && companySourceOptionsQueryError) {
            setNotification({ text: companySourceOptionsQueryError.message, level: 'error' });
        }
    }, [companySourceOptionsError, companySourceOptionsQueryError]);

    useEffect(() => {
        if (palletsError && palletsQueryError) {
            setNotification({ text: palletsQueryError.message, level: 'error' });
        }
    }, [palletsError, palletsQueryError]);

    useEffect(() => {
        setRowSelections({});
        setValidationError(null);
    }, [locationId, lotId, inventoryRowsKey]);

    const effectiveTargetCompanySourceId = useMemo(() => {
        if (targetCompanySourceId) {
            return targetCompanySourceId;
        }
        if (effectiveCompanySourceOptions.length === 1) {
            return String(effectiveCompanySourceOptions[0].value);
        }
        return '';
    }, [effectiveCompanySourceOptions, targetCompanySourceId]);

    const selectedRowCount = useMemo(
        () => rowMeta.filter(({ key }) => rowSelections[key]?.selected).length,
        [rowMeta, rowSelections],
    );

    const allRowsSelected = rowMeta.length > 0 && selectedRowCount === rowMeta.length;
    const someRowsSelected = selectedRowCount > 0 && !allRowsSelected;

    const selectAllChecked = useMemo(() => {
        if (allRowsSelected) {
            return true;
        }
        if (someRowsSelected) {
            return 'indeterminate';
        }
        return false;
    }, [allRowsSelected, someRowsSelected]);

    const targetCompanySourceField = useMemo(() => {
        if (companySourceOptionsLoading) {
            return (
                <div className="flex min-h-9 items-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading company sources…</span>
                </div>
            );
        }

        if (effectiveCompanySourceOptions.length === 0) {
            return (
                <Alert variant="destructive" className="mt-4">
                    <AlertCircle aria-hidden="true" />
                    <AlertTitle>Company source transfer unavailable</AlertTitle>
                    <AlertDescription>
                        No other company sources are configured for this item, so a transfer cannot be completed.
                    </AlertDescription>
                </Alert>
            );
        }

        return (
            <Select value={effectiveTargetCompanySourceId} onValueChange={setTargetCompanySourceId}>
                <SelectTrigger id="target-company-source" className="w-full justify-between">
                    <SelectValue placeholder="Select target company source" />
                </SelectTrigger>
                <SelectContent>
                    {effectiveCompanySourceOptions.map((option) => (
                        <SelectItem key={String(option.value)} value={String(option.value)}>
                            {option.text}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }, [companySourceOptionsLoading, effectiveCompanySourceOptions, effectiveTargetCompanySourceId]);

    const handleRowSelectionChange = useCallback((rowKey, checked, availableCases, availableLooseUnits) => {
        setRowSelections((prev) => ({
            ...prev,
            [rowKey]: {
                selected: checked,
                numCases: checked ? availableCases : 0,
                numLooseUnits: checked ? availableLooseUnits : 0,
            },
        }));
        setValidationError(null);
    }, []);

    const handleSelectAllChange = useCallback(
        (checked) => {
            setRowSelections((prev) => {
                const next = { ...prev };
                for (const { availableCases, availableLooseUnits, key } of rowMeta) {
                    next[key] = {
                        selected: checked,
                        numCases: checked ? availableCases : 0,
                        numLooseUnits: checked ? availableLooseUnits : 0,
                    };
                }
                return next;
            });
            setValidationError(null);
        },
        [rowMeta],
    );

    const handleQuantityChange = useCallback((rowKey, field, rawValue, maxValue) => {
        const parsed = parseNonNegativeInt(rawValue);
        const clamped = Math.min(parsed, maxValue);

        setRowSelections((prev) => ({
            ...prev,
            [rowKey]: {
                selected: prev[rowKey]?.selected ?? false,
                numCases: field === 'numCases' ? clamped : (prev[rowKey]?.numCases ?? 0),
                numLooseUnits: field === 'numLooseUnits' ? clamped : (prev[rowKey]?.numLooseUnits ?? 0),
            },
        }));
        setValidationError(null);
    }, []);

    const validateSubmission = useCallback(() => {
        if (!effectiveTargetCompanySourceId) {
            return 'Select a target company source.';
        }

        const selectedRows = rowMeta.filter(({ key }) => rowSelections[key]?.selected);
        if (!selectedRows.length) {
            return 'Select at least one inventory row to transfer.';
        }

        for (const { availableCases, availableLooseUnits, key } of selectedRows) {
            const selection = rowSelections[key];
            const selectedCases = selection?.numCases ?? 0;
            const selectedLooseUnits = selection?.numLooseUnits ?? 0;

            if (selectedCases === 0 && selectedLooseUnits === 0) {
                return 'Each selected row must transfer at least one case or loose unit.';
            }
            if (selectedCases > availableCases) {
                return `Transfer cases cannot exceed available cases (${availableCases}).`;
            }
            if (selectedLooseUnits > availableLooseUnits) {
                return `Transfer loose units cannot exceed available loose units (${availableLooseUnits}).`;
            }
        }

        return null;
    }, [effectiveTargetCompanySourceId, rowMeta, rowSelections]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        const error = validateSubmission();
        if (error) {
            setValidationError(error);
            return;
        }

        setValidationError(null);

        const selectedInventory = rowMeta
            .filter(({ key }) => rowSelections[key]?.selected)
            .map(({ key, row }) => {
                const selection = rowSelections[key];
                const inventoryItem = {
                    numCases: selection.numCases,
                    numLooseUnits: selection.numLooseUnits,
                };

                if (row.palletId !== undefined && row.palletId !== null && row.palletId !== '') {
                    inventoryItem.palletId = row.palletId;
                }

                return inventoryItem;
            })
            .filter((inventoryItem) => inventoryItem.numCases > 0 || inventoryItem.numLooseUnits > 0);

        pendingTransferredCasesRef.current = selectedInventory.reduce((sum, item) => sum + item.numCases, 0);

        transferMutation.mutate({
            sourceLotId: lotId,
            destinationCompanySourceId: Number(effectiveTargetCompanySourceId),
            locationId,
            selectedInventory,
        });
    };

    const handleNumLabelsChange = (event) => {
        const parsed = parseNonNegativeInt(event.target.value);
        setNumLabels(parsed > 0 ? parsed : 0);
    };

    const handlePrintCaseLabels = async () => {
        if (!transferResult?.lotId) {
            return;
        }

        if (numLabels <= 0) {
            setNotification({ text: 'Enter at least one label to print.', level: 'error' });
            return;
        }

        setPrintingLabels(true);
        const result = await labelApi.getCaseLabels({ lotID: transferResult.lotId, qty: numLabels });
        setPrintingLabels(false);

        if (result instanceof Blob) {
            printPdfBlob(result);
            return;
        }

        setNotification({ text: 'There was an issue printing the labels.', level: 'error' });
    };

    const handleDone = () => {
        if (onCancel instanceof Function) {
            onCancel();
        }
    };

    const isSubmitting = transferMutation.isPending;
    const isLoadingInventoryRows =
        shouldFetchPallets && !inventoryRowsOverride?.length && (palletsLoading || palletsFetching);

    const inventoryTableSection = useMemo(() => {
        if (isLoadingInventoryRows) {
            return (
                <div className="flex min-h-24 items-center justify-center gap-2 rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-3 py-6 text-muted-foreground">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading pallet inventory…</span>
                </div>
            );
        }

        if (rowMeta.length === 0) {
            return (
                <p className="rounded-md border border-dashed border-muted-foreground/40 bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                    No inventory rows available for transfer.
                </p>
            );
        }

        return (
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow className="sticky top-0 z-10 bg-background shadow-sm">
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={selectAllChecked}
                                    onCheckedChange={(checked) => handleSelectAllChange(checked === true)}
                                    aria-label="Select all rows"
                                />
                            </TableHead>
                            <TableHead>Pallet</TableHead>
                            <TableHead>Available cases</TableHead>
                            <TableHead>Available loose units</TableHead>
                            <TableHead>Transfer cases</TableHead>
                            <TableHead>Transfer loose units</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rowMeta.map(({ availableCases, availableLooseUnits, key, row }) => {
                            const selection = rowSelections[key];
                            const isSelected = selection?.selected ?? false;
                            const transferCases = selection?.numCases ?? 0;
                            const transferLooseUnits = selection?.numLooseUnits ?? 0;
                            const palletLabel =
                                row.palletId !== undefined && row.palletId !== null && row.palletId !== ''
                                    ? String(row.palletId)
                                    : '—';

                            return (
                                <TableRow key={key} data-state={isSelected ? 'selected' : undefined}>
                                    <TableCell>
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked) =>
                                                handleRowSelectionChange(
                                                    key,
                                                    checked === true,
                                                    availableCases,
                                                    availableLooseUnits,
                                                )
                                            }
                                            aria-label={`Select row ${palletLabel}`}
                                        />
                                    </TableCell>
                                    <TableCell>{palletLabel}</TableCell>
                                    <TableCell className="tabular-nums">{availableCases}</TableCell>
                                    <TableCell className="tabular-nums">{availableLooseUnits}</TableCell>
                                    <TableCell>
                                        <Input
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            max={availableCases}
                                            disabled={!isSelected || isSubmitting}
                                            className="w-24 tabular-nums"
                                            aria-label={`Transfer cases for ${palletLabel}`}
                                            value={isSelected ? String(transferCases) : '0'}
                                            onChange={(e) =>
                                                handleQuantityChange(key, 'numCases', e.target.value, availableCases)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            max={availableLooseUnits}
                                            disabled={!isSelected || isSubmitting}
                                            className="w-24 tabular-nums"
                                            aria-label={`Transfer loose units for ${palletLabel}`}
                                            value={isSelected ? String(transferLooseUnits) : '0'}
                                            onChange={(e) =>
                                                handleQuantityChange(
                                                    key,
                                                    'numLooseUnits',
                                                    e.target.value,
                                                    availableLooseUnits,
                                                )
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    }, [
        handleQuantityChange,
        handleRowSelectionChange,
        handleSelectAllChange,
        isLoadingInventoryRows,
        isSubmitting,
        rowMeta,
        rowSelections,
        selectAllChecked,
    ]);

    if (transferResult) {
        return (
            <div className="flex h-full min-h-0 flex-col text-left">
                <Notifications options={notification} />

                <div className="shrink-0 space-y-4 p-3">
                    <Alert className="border-green-500 bg-green-50 text-green-900 [&>svg]:text-green-600 *:data-[slot=alert-description]:text-green-800">
                        <CheckCircle2 aria-hidden="true" />
                        <AlertTitle>Company source transfer completed</AlertTitle>
                        <AlertDescription>
                            Inventory was transferred successfully
                            {transferResult.lotId ? (
                                <>
                                    {' '}
                                    to lot <span className="font-medium">{transferResult.lotId}</span>
                                </>
                            ) : null}
                            .
                        </AlertDescription>
                    </Alert>

                    {transferResult.lotId ? (
                        <div className="space-y-3 rounded-md border p-4">
                            <div>
                                <h4 className="text-sm font-medium text-foreground">Print case labels</h4>
                                <p className="text-sm text-muted-foreground">
                                    Print labels for the transferred inventory on the destination lot.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor="num-case-labels">Number of labels</Label>
                                    <Input
                                        id="num-case-labels"
                                        type="number"
                                        inputMode="numeric"
                                        min={1}
                                        className="w-28 tabular-nums"
                                        value={numLabels > 0 ? String(numLabels) : ''}
                                        onChange={handleNumLabelsChange}
                                        disabled={printingLabels}
                                    />
                                </div>
                                <Button
                                    type="button"
                                    onClick={handlePrintCaseLabels}
                                    disabled={printingLabels || numLabels <= 0}
                                >
                                    {printingLabels ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Printing…
                                        </>
                                    ) : (
                                        'Print case labels'
                                    )}
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="flex shrink-0 justify-end border-t p-3">
                    <Button type="button" onClick={handleDone}>
                        Done
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col text-left">
            <Notifications options={notification} />

            <div className="shrink-0 space-y-4 p-3">
                <div>
                    <h3 className="text-lg font-semibold text-foreground">Transfer Company Source</h3>
                    <p className="text-sm text-muted-foreground">
                        From <span className="font-medium text-foreground">{currentCompanySourceName}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Location: <span className="font-medium text-foreground">{locationName}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {externalSku ? 'External Sku' : 'Packaging Id'}:
                        <span className="font-medium text-foreground">{externalSku || packagingId}</span>
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="target-company-source">Target company source</Label>
                    {targetCompanySourceField}
                </div>

                {validationError ? (
                    <Alert variant="destructive">
                        <AlertDescription>{validationError}</AlertDescription>
                    </Alert>
                ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3">{inventoryTableSection}</div>

            <div className="flex shrink-0 justify-end border-t p-3">
                <Button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                        isSubmitting ||
                        isLoadingInventoryRows ||
                        companySourceOptionsLoading ||
                        effectiveCompanySourceOptions.length === 0 ||
                        rowMeta.length === 0
                    }
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            Submitting…
                        </>
                    ) : (
                        'Submit transfer'
                    )}
                </Button>
            </div>
        </div>
    );
}

export { CompanySourceTransferAction };
